import { type FC, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  ContentTypeType,
  PollTriggerType,
} from "@/types/graphql/partner.generated";
import {
  pollErrorKey,
  useCreateContentPollMutation,
  usePollsListQuery,
  useUpdateContentPollMutation,
  type ContentPoll,
} from "@/partner/entities/polls";
import { PagePaths } from "@/partner/shared/constants";
import {
  CONTENT_POLL_FORM_ERROR_KEYS,
  contentPollToFormValues,
  emptyContentPollForm,
  POLL_TRIGGER_LABEL_KEYS,
  toScheduleInput,
  validateContentPollForm,
  type ContentPollFormValues,
} from "../model/contentPollForm";

interface AttachPollDialogProps {
  contentId: string;
  contentType: ContentTypeType;
  /** null: прикрепить новый опрос из библиотеки. */
  contentPoll: ContentPoll | null;
  onClose: () => void;
}

/**
 * Выбор опроса из библиотеки (селект, как WidgetField) и его запуск у
 * этого видео или эфира. У существующего прикрепления меняется только
 * запуск; чтобы сменить опрос, прикрепление удаляют и добавляют новое.
 */
export const AttachPollDialog: FC<AttachPollDialogProps> = ({
  contentId,
  contentType,
  contentPoll,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [values, setValues] = useState<ContentPollFormValues>(() =>
    contentPoll
      ? contentPollToFormValues(contentPoll)
      : emptyContentPollForm(contentType)
  );
  const [submitted, setSubmitted] = useState(false);
  const isVideo = contentType === ContentTypeType.VideoType;
  const error = validateContentPollForm(values, contentType);

  // Библиотека платформы обычно короткая: первой сотни хватает, как в
  // селекте виджета.
  const { data: libraryData, loading: libraryLoading } = usePollsListQuery({
    variables: { page: 0, rowsPerPage: 100 },
    skip: Boolean(contentPoll),
  });
  const library = libraryData?.polls.nodes ?? [];
  const libraryEmpty = !contentPoll && !libraryLoading && library.length === 0;

  const onCompleted = () => {
    enqueueSnackbar(t("polls.snackbar.saved"), { variant: "success" });
    onClose();
  };
  const onError = (mutationError: Error) =>
    enqueueSnackbar(t(pollErrorKey(mutationError)), { variant: "error" });
  const [createContentPoll, { loading: creating }] =
    useCreateContentPollMutation({ onCompleted, onError });
  const [updateContentPoll, { loading: updating }] =
    useUpdateContentPollMutation({ onCompleted, onError });

  const set = <K extends keyof ContentPollFormValues>(
    key: K,
    value: ContentPollFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    setSubmitted(true);
    if (error) return;
    const input = toScheduleInput(values, contentType);
    if (contentPoll) {
      void updateContentPoll({ variables: { id: contentPoll.id, input } });
    } else {
      void createContentPoll({
        variables: { contentId, pollId: values.pollId, input },
      });
    }
  };

  const triggers = isVideo
    ? [PollTriggerType.AtOffsetType]
    : Object.values(PollTriggerType);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {contentPoll
              ? t("polls.attach.editTitle")
              : t("polls.attach.title")}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            disabled={creating || updating || libraryEmpty}
          >
            {t("common.actions.save")}
          </Button>
        </Toolbar>
        <Divider />
      </AppBar>
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {contentPoll ? (
            <TextField
              label={t("polls.attach.poll")}
              value={contentPoll.question}
              disabled
              fullWidth
            />
          ) : libraryEmpty ? (
            <Stack spacing={1} alignItems="flex-start">
              <Typography color="text.secondary">
                {t("polls.attach.libraryEmpty")}
              </Typography>
              <Button component={RouterLink} to={PagePaths.polls}>
                {t("polls.attach.openLibrary")}
              </Button>
            </Stack>
          ) : (
            <TextField
              select
              label={t("polls.attach.poll")}
              value={values.pollId}
              onChange={(event) => set("pollId", event.target.value)}
              fullWidth
            >
              {library.map((poll) => (
                <MenuItem key={poll.id} value={poll.id}>
                  {poll.question}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            label={t("polls.form.trigger")}
            value={isVideo ? PollTriggerType.AtOffsetType : values.trigger}
            disabled={isVideo}
            onChange={(event) =>
              set("trigger", event.target.value as PollTriggerType)
            }
            fullWidth
          >
            {triggers.map((trigger) => (
              <MenuItem key={trigger} value={trigger}>
                {t(POLL_TRIGGER_LABEL_KEYS[trigger])}
              </MenuItem>
            ))}
          </TextField>
          {(isVideo || values.trigger === PollTriggerType.AtOffsetType) && (
            <TextField
              label={
                isVideo
                  ? t("polls.form.offsetVideo")
                  : t("polls.form.offsetStream")
              }
              helperText={t("polls.form.offsetHint")}
              value={values.offset}
              onChange={(event) => set("offset", event.target.value)}
              fullWidth
            />
          )}
          {!isVideo && values.trigger === PollTriggerType.AtTimeType && (
            // Прошедшее время допустимо: опрос откроется сразу.
            <DateTimePicker
              label={t("polls.form.startsAt")}
              ampm={false}
              value={values.startsAt}
              onChange={(value) => set("startsAt", value)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          )}
          {!isVideo && (
            <TextField
              type="number"
              label={t("polls.form.autoClose")}
              helperText={t("polls.form.autoCloseHint")}
              value={values.autoCloseSeconds}
              onChange={(event) => set("autoCloseSeconds", event.target.value)}
              fullWidth
            />
          )}
          {submitted && error && (
            <Box>
              <Typography color="error" variant="body2">
                {t(CONTENT_POLL_FORM_ERROR_KEYS[error])}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
